import React from "react";
import { QuestionnaireManagementErrors, ValidateQuestionnairePropertiesMetadata, useQuestionnaireValidationController } from "./questionnaire-validation";
import { QuestionnaireUpdateLocalDto } from "../dto/questionnaire-update-local-dto";
import { QuestionnaireUpdateDto } from "@common/dto/questionnaire/update-dto";
import { QuestionnairePageCreateDto } from "@common/dto/questionnaire/page/create-dto";
import { QuestionnairePageCreateLocalDto } from "../dto/page-create-local-dto";
import { QuestionnairePageUpdateDto } from "@common/dto/questionnaire/page/update-dto";
import { QuestionnairePageDeleteDto } from "@common/dto/questionnaire/page/delete-dto";
import { QuestionnairePageListDto } from "@common/dto/questionnaire/page/list-dto";
import { QuestionnaireGetExtendedDto } from "@common/dto/questionnaire/get-extended-dto";
import { QuestionnaireAffectedFamilyPageCreateDto } from "@common/dto/questionnaire/page/affected-family/create-dto";
import { QuestionnaireCousinPageCreateDto } from "@common/dto/questionnaire/page/cousin/create-dto";
import { QuestionnaireFamilyBuilderPageCreateDto } from "@common/dto/questionnaire/page/family-builder/create-dto";
import { QuestionnaireHalfSiblingPageCreateDto } from "@common/dto/questionnaire/page/half-sibling/create-dto";
import { QuestionnaireNieceNephewPageCreateDto } from "@common/dto/questionnaire/page/niece-nephew/create-dto";
import { QuestionnaireDefaultCreateDto } from "@common/dto/questionnaire/default-create-dto";
import { QuestionnaireCreateLocalDto } from "../dto/questionnaire-create-local-dto";
import { QuestionnaireCreateDto } from "@common/dto/questionnaire/create-dto";
import { QuestionnaireType } from "@common/dto/questionnaire/questionnaire-type";
import { QuestionnaireDesignerTabUpdateLocalDto } from "../dto/designer-update-local-dto";
import { generateId } from "@common/utils/common";
import { QuestionnaireLanguage } from "@common/dto/questionnaire/language";
import { exportQuestionnaire } from "../services/questionnaires";
import { I18n } from "react-redux-i18n";
import { downloadFile } from "@common/utils/file";
import { MessageShowParams } from "@common/hooks/message";
import { QuestionnairePageType } from "@common/dto/questionnaire/page/type";
import { QuestionnaireTextBlockLocalDto } from "../dto/text-block-create-dto";
import { QuestionnaireTextBlockCreateDto } from "@common/dto/questionnaire/text-block/create-dto";
import { QuestionnaireTextBlockListDto } from "@common/dto/questionnaire/text-block/list-dto";
import { QuestionnaireTextBlockUpdateDto } from "@common/dto/questionnaire/text-block/update-dto";

class QuestionnaireHolder {
  private questionnaire?: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto;

  get = () => {
    return this.questionnaire;
  }

  set = (questionnaire?: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto) => {
    this.questionnaire = questionnaire;
  }
}

type QuestionnaireControllerProps = {
  selectedQuestionnaire?: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto;
  showErrorMessage: (message: string) => void;
  showMessage: (params: MessageShowParams) => void;
  closeMessage: () => void;
};

export type ValidateQuestionnaireMetadata = ValidateQuestionnairePropertiesMetadata;

export const useQuestionnaireController = (props: QuestionnaireControllerProps) => {
  const LOCAL_CREATING_QUESTIONNAIRE_ID = -1;
  const { selectedQuestionnaire, showErrorMessage, showMessage, closeMessage } = props;
  const [questionnaireManagementValidationErrors, setQuestionnaireManagementValidationErrors] = React.useState<QuestionnaireManagementErrors | undefined>();
  const [questionnaireManagementValidationInProgress, setQuestionnaireManagementValidationInProgress] = React.useState(false);
  const questionnaireHolder = React.useMemo(() => new QuestionnaireHolder(), []);
  const [loadedPages, setLoadedPages] = React.useState<Array<QuestionnairePageUpdateDto['id']>>([]);
  const [questionnaireHasBeenChanged, setQuestionnaireHasBeenChanged] = React.useState(false);
  
  const { validateQuestionnaire: validateQuestionnaireBase } = useQuestionnaireValidationController();

  const handleLocalQuestionnaireChange = React.useCallback((
    questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto,
    params?: { operationType?: 'addExistingInfo' | 'change' }
  ) => {
    questionnaireHolder.set(questionnaire);

    if (params?.operationType === 'change') {
      setQuestionnaireHasBeenChanged(true);
    }
  }, []);

  const handleSetLocalQuestionnaire = React.useCallback((questionnaire: QuestionnaireGetExtendedDto | QuestionnaireCreateLocalDto) => {
    questionnaireHolder.set(questionnaire);
    setLoadedPages([]);
    setQuestionnaireManagementValidationErrors(undefined);
    setQuestionnaireHasBeenChanged(false);
  }, []);

  const resetLocalQuestionnaire = React.useCallback(() => {
    questionnaireHolder.set(undefined);
    setLoadedPages([]);
    setQuestionnaireManagementValidationErrors(undefined);
    setQuestionnaireHasBeenChanged(false);
  }, []);

  const validateQuestionnaire = React.useCallback(async (
    questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireUpdateDto | QuestionnaireCreateDto | QuestionnaireCreateLocalDto, 
    metadata: ValidateQuestionnaireMetadata
  ): Promise<boolean> => {
    try {
      setQuestionnaireManagementValidationInProgress(true);

      const errors = await validateQuestionnaireBase(questionnaire, metadata);

      setQuestionnaireManagementValidationErrors(errors);
      setQuestionnaireManagementValidationInProgress(false);

      const hasAnyError = Boolean(errors);

      return hasAnyError;
    } catch (err) {
      console.error(err);
      setQuestionnaireManagementValidationInProgress(false);
      return false;
    }
  }, [validateQuestionnaireBase]);

  React.useEffect(() => {
    questionnaireHolder.set(selectedQuestionnaire);
  }, [selectedQuestionnaire]);

  const hasQuestionnaireUnsavedChanges = () => {
    if (selectedQuestionnaire && questionnaireHolder.get()) {
      return questionnaireHasBeenChanged;
    }

    return false;
  };

  type AnyText = QuestionnaireTextBlockLocalDto | QuestionnaireTextBlockCreateDto | QuestionnaireTextBlockUpdateDto | QuestionnaireTextBlockListDto;

  const processPageTexts = React.useCallback(
    (
      texts?: Array<AnyText>
    ): undefined | Array<QuestionnaireTextBlockCreateDto | QuestionnaireTextBlockUpdateDto | QuestionnaireTextBlockListDto> => {
    if (!texts) {
      return undefined;
    }
    
    const textBlocks = texts.map((text) => {
      const notCreatedText = text as QuestionnaireTextBlockLocalDto;

      if (notCreatedText.isNotCreated) {
        const { id, isNotCreated, ...rest } = notCreatedText;

        return rest;
      }

      return text;
    });

    return textBlocks;
  }, []);

  const getQuestionnaireDtoConvertedToOriginalDto = (): QuestionnaireUpdateDto | QuestionnaireCreateDto | void => {
    const localQuestionnaireDto = questionnaireHolder.get();

    if (localQuestionnaireDto && !(localQuestionnaireDto as QuestionnaireCreateLocalDto ).isNotCreated) {
      const { designerTab: { pages, ...otherDesignerTab }, ...otherQuestionnaire } = localQuestionnaireDto as QuestionnaireUpdateLocalDto ;
      const updateQuestionnaireDto: QuestionnaireUpdateDto = {
        ...otherQuestionnaire,
        consentTab: {
          ...otherQuestionnaire.consentTab,
          texts: processPageTexts(otherQuestionnaire.consentTab.texts),
        },
        designerTab: {
          ...otherDesignerTab,
          pages: pages.map((page) => {
            const pageToPotentialCreating = page as QuestionnairePageCreateLocalDto;

            if (pageToPotentialCreating.isNotCreated) {
              type PageToCreateDto = 
                QuestionnairePageCreateDto | 
                QuestionnaireAffectedFamilyPageCreateDto | 
                QuestionnaireCousinPageCreateDto | 
                QuestionnaireFamilyBuilderPageCreateDto | 
                QuestionnaireHalfSiblingPageCreateDto |
                QuestionnaireNieceNephewPageCreateDto;
                
              const pageCreateDto: PageToCreateDto = {
                title: pageToPotentialCreating.title,
                description: pageToPotentialCreating.description,
                pageType: pageToPotentialCreating.pageType,
                order: pageToPotentialCreating.order,
                fields: pageToPotentialCreating.fields,
                skipLogic: pageToPotentialCreating.skipLogic,
                skipLogicEnabled: pageToPotentialCreating.skipLogicEnabled,
                modified: true,
                enabled: pageToPotentialCreating.enabled,
                texts: processPageTexts(pageToPotentialCreating.texts),
              };
              
              return pageCreateDto;
            }
  
            return {
              ...page,
              texts: processPageTexts((page as QuestionnairePageUpdateDto).texts),
            } as QuestionnairePageUpdateDto | QuestionnairePageDeleteDto | QuestionnairePageListDto;
          })
        },
      };

      return updateQuestionnaireDto;
    }

    if (localQuestionnaireDto && (localQuestionnaireDto as QuestionnaireCreateLocalDto ).isNotCreated) {
      const { isNotCreated, designerTab: { pages, ...otherDesignerTab }, ...otherQuestionnaire } = localQuestionnaireDto as QuestionnaireCreateLocalDto;
      const updateQuestionnaireDto: QuestionnaireCreateDto = {
        ...otherQuestionnaire,
        enabled: otherQuestionnaire.enabled ?? true,
        questionnaireId: 0,
        consentTab: {
          ...otherQuestionnaire.consentTab,
          texts: processPageTexts(otherQuestionnaire.consentTab.texts),
        },
        designerTab: {
          ...otherDesignerTab,
          pages: pages.map((page) => {
            const pageToPotentialCreating = page as QuestionnairePageCreateLocalDto;

            if (pageToPotentialCreating.isNotCreated) {
              type PageToCreateDto = 
                QuestionnairePageCreateDto | 
                QuestionnaireAffectedFamilyPageCreateDto | 
                QuestionnaireCousinPageCreateDto | 
                QuestionnaireFamilyBuilderPageCreateDto | 
                QuestionnaireHalfSiblingPageCreateDto |
                QuestionnaireNieceNephewPageCreateDto;
                
              const pageCreateDto: PageToCreateDto = {
                title: pageToPotentialCreating.title,
                description: pageToPotentialCreating.description,
                pageType: pageToPotentialCreating.pageType,
                order: pageToPotentialCreating.order,
                fields: pageToPotentialCreating.fields,
                skipLogic: pageToPotentialCreating.skipLogic,
                skipLogicEnabled: pageToPotentialCreating.skipLogicEnabled,
                modified: true,
                enabled: pageToPotentialCreating.enabled,
                texts: processPageTexts(pageToPotentialCreating.texts),
              };
              
              return pageCreateDto;
            }
  
            return {
              ...page,
              texts: processPageTexts((page as QuestionnairePageUpdateDto).texts),
            } as QuestionnairePageUpdateDto | QuestionnairePageDeleteDto | QuestionnairePageListDto;
          })
        },
      };

      return updateQuestionnaireDto;
    }
  };

  const markPageLoaded = (pageId: QuestionnairePageUpdateDto['id']) => {
    setLoadedPages((pages) => [...pages, pageId]);
  };

  const resetPagesLoadedState = () => {
    setLoadedPages([]);
  };

  const resetQuestionnaireChangedState = () => {
    setQuestionnaireHasBeenChanged(false);
  };

  const isPageLoaded = React.useCallback((pageId: QuestionnairePageUpdateDto['id']) => {
    return loadedPages.includes(pageId);
  }, [loadedPages]);

  const convertQuestionnaireDefaultCreateDtoToQuestionnaireCreateLocalDto = (questionnaire: QuestionnaireDefaultCreateDto): QuestionnaireCreateLocalDto => {
    const defaultDescription: Partial<Record<QuestionnairePageType, string>> = {
      [QuestionnairePageType.affected]: `${I18n.t('Please add all family members who have cancer')}.`,
      [QuestionnairePageType.cousin]: `${I18n.t('Please add the number of sons and/or daughters for each of your aunts/uncles')}.`,
      [QuestionnairePageType.familyBuilder]: `${I18n.t('Please provide the number of blood relatives you have for each of the following relations')}. ${I18n.t('You will be able to add or remove each relative one-by-one, however, in the subsequent sections')}.`,
      [QuestionnairePageType.familyMembers]: `${I18n.t('Please add all family members who have cancer')}.`,
      [QuestionnairePageType.halfSibling]: `${I18n.t('Please enter the information below regarding your half-sibling and which parent you share')}.`,
      [QuestionnairePageType.nieceNephew]: `${I18n.t('Please add the number of sons and/or daughters for each of your aunts/uncles')}.`,
      [QuestionnairePageType.finalNotes]: `${I18n.t('Please use this page to tell us about any other family history that you have not already listed')}. ${I18n.t('You can also use this page for any questions or comments that you would like to share with your genetic counselor')}.`,
    };
    
    const questionnaireDesignerPages: Array<QuestionnairePageCreateLocalDto> = questionnaire.designerTab.pages.map((page) => {
      const pageDto: QuestionnairePageCreateLocalDto = {
        id: generateId(),
        title: page.title,
        description: page.description || defaultDescription[page.pageType],
        pageType: page.pageType,
        order: page.order,
        fields: page.fields,
        skipLogicEnabled: page.skipLogicEnabled,
        skipLogic: page.skipLogic,
        modified: true,
        enabled: page.enabled,
        isNotCreated: true,
      };

      return pageDto;
    });

    const designerTab: QuestionnaireDesignerTabUpdateLocalDto = {
      pages: questionnaireDesignerPages,
    };
    
    const resultQuestionnaire: QuestionnaireCreateLocalDto = {
      enabled: true,
      isNotCreated: true,
      questionnaireId: LOCAL_CREATING_QUESTIONNAIRE_ID,
      questionnaireName: '',
      questionnaireType: QuestionnaireType.family,
      fhq4: true,
      homeTab: {
        localeCode: QuestionnaireLanguage.enUS,
        ...questionnaire.homeTab
      },
      configurationTab: questionnaire.configurationTab,
      emailsTab: questionnaire.emailsTab,
      statusTab: questionnaire.statusTab,
      designerTab: designerTab,
      consentTab: questionnaire.consentTab as any,
    };

    return resultQuestionnaire;
  };

  const handleExportQuestionnaire = async () => {
    if (!selectedQuestionnaire) {
      return;
    }

    await new Promise<void>(async (resolve) => {
      showMessage({
        header: I18n.t('Export Questionnaire Format'),
        content: `${selectedQuestionnaire.questionnaireName} ${I18n.t('will be exported')}`,
        confirmText: I18n.t('Export'),
        cancelText: I18n.t('Cancel'),
        hideMessageBox: () => {
          closeMessage();
          resolve();
        },
        onCustomOk: async () => {
          closeMessage();
          exportQuestionnaire(selectedQuestionnaire.questionnaireId)
            .then((file) => {
              downloadFile(file, `${selectedQuestionnaire.questionnaireName}__${new Date().toLocaleDateString()}.xml`, 'text/xml');
            })
            .catch((err) => {
              console.error(err);
              showErrorMessage(I18n.t('Failed to export questionnaire'));
            })
            .finally(() => {
              resolve();
            });
        }
      }
    )});
  };

  return {
    resetQuestionnaireChangedState,
    questionnaireHasBeenChanged,
    handleExportQuestionnaire,
    convertQuestionnaireDefaultCreateDtoToQuestionnaireCreateLocalDto,
    resetPagesLoadedState,
    isPageLoaded,
    markPageLoaded,
    validateQuestionnaire,
    questionnaireManagementValidationErrors,
    questionnaireManagementValidationInProgress,
    getQuestionnaireDtoConvertedToOriginalDto,
    hasQuestionnaireUnsavedChanges,
    handleLocalQuestionnaireChange,
    resetLocalQuestionnaire,
    handleSetLocalQuestionnaire,
  };
};